// 玩法 Demo motion graphic（AdminGodfather「玩法 Demo」TAB 用）
// 照 couponly /demo 哲學：故事地圖一圖看＋手機畫面自動輪播＋一句 caption；
// 手動操作即停自動播放；背景音樂可開關（瀏覽器播放限制＝需點擊啟用）
import { useEffect, useMemo, useState } from 'react';
import { StoryStyles, PhoneFrame, BgmToggle } from './GfStoryPhone';
import { mailScenes, pickupScenes, type GfScene } from './godfatherDemoScenes';

type Mode = 'pickup' | 'mail';

/** 故事地圖——整章旅程一圖看清：過去＝實心、現在＝放大脈衝、未來＝淡圈；可點跳幕 */
function FlowMap({ scenes, active, onJump }: { scenes: GfScene[]; active: number; onJump: (i: number) => void }) {
  return (
    <div className="flex items-center justify-start gap-0 overflow-x-auto py-1 sm:justify-center">
      {scenes.map((s, i) => (
        <div key={`${s.node}-${i}`} className="flex shrink-0 items-center">
          <button onClick={() => onJump(i)} aria-label={s.node} className="flex w-[62px] flex-col items-center gap-1">
            <span
              className={`flex h-10 w-10 items-center justify-center rounded-full text-lg transition-all duration-300 ${
                i === active
                  ? 'demo-dot scale-110 text-white shadow-lg ring-4 ring-red-300/60'
                  : i < active
                    ? 'text-white'
                    : 'bg-white text-stone-400 ring-1 ring-stone-300 dark:bg-[#231a1a] dark:ring-[#3b2e2e]'
              }`}
              style={i <= active ? { background: '#d9381e' } : undefined}
            >
              {s.icon}
            </span>
            <span
              className={`text-center text-[9px] leading-tight ${
                i === active ? 'font-black' : 'text-stone-400 dark:text-gray-500'
              }`}
              style={i === active ? { color: '#d9381e' } : undefined}
            >
              {s.node}
            </span>
          </button>
          {i < scenes.length - 1 && (
            <span
              className={`mx-0.5 h-[3px] w-3 rounded-full sm:w-6 ${i < active ? '' : 'bg-stone-200 dark:bg-[#3b2e2e]'}`}
              style={i < active ? { background: '#d9381e' } : undefined}
            />
          )}
        </div>
      ))}
    </div>
  );
}

export default function GodfatherDemo() {
  const [mode, setMode] = useState<Mode>('pickup');
  const [idx, setIdx] = useState(0);
  const [auto, setAuto] = useState(true);

  const scenes = useMemo(() => (mode === 'pickup' ? pickupScenes() : mailScenes()), [mode]);
  const scene = scenes[Math.min(idx, scenes.length - 1)];
  const total = scenes.length;

  useEffect(() => setIdx(0), [mode]);

  useEffect(() => {
    if (!auto) return;
    const timer = window.setInterval(() => setIdx((i) => (i + 1) % total), 5400);
    return () => window.clearInterval(timer);
  }, [auto, total]);

  /** 一切手動導航＝停自動播放 */
  const jump = (i: number) => {
    setAuto(false);
    setIdx(Math.max(0, Math.min(total - 1, i)));
  };

  return (
    <div className="space-y-4">
      <StoryStyles />
      <BgmToggle />

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4">
        <h2 className="font-bold text-gray-800 dark:text-white flex items-center gap-2">
          🎬 玩法 Demo——一分鐘睇晒成個流程
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          邊步客人做、邊步系統自動、邊步你要郁手，逐幕自動播放：
          <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">🟦 客人做</span>
          <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">🟢 系統自動</span>
          <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">🟡 你要郁手</span>
        </p>
        <div className="mt-3 flex gap-1">
          {([['pickup', '🏪 到店自選兌換'], ['mail', '📮 郵寄']] as const).map(([m, label]) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`px-4 py-1.5 rounded-lg text-sm font-bold transition ${mode === m ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* 故事地圖——全章一圖 */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-3">
        <FlowMap scenes={scenes} active={idx} onJump={jump} />
      </div>

      <div className="flex flex-col items-center gap-6 lg:flex-row lg:items-center lg:justify-center">
        <PhoneFrame key={`${mode}-${idx}`} label={scene.phoneLabel}>
          {scene.phone}
        </PhoneFrame>

        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3.5">
            <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl text-3xl text-white shadow-lg" style={{ background: '#d9381e' }}>
              {scene.icon}
            </span>
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-widest text-stone-400">
                {mode === 'pickup' ? '到店自選兌換' : '郵寄'}
              </div>
              <div className="truncate text-xl font-black text-stone-800 dark:text-stone-100">{scene.node}</div>
            </div>
          </div>

          <p className="demo-pop mt-4 text-[15px] font-bold leading-relaxed text-stone-800 dark:text-stone-100" key={`cap-${mode}-${idx}`}>
            {scene.caption}
          </p>

          <div className="mt-6 flex items-center gap-2">
            <button
              onClick={() => jump(idx - 1)}
              disabled={idx === 0}
              aria-label="上一幕"
              className="rounded-xl bg-white px-3.5 py-2 text-sm font-bold text-stone-600 shadow ring-1 ring-stone-200 disabled:opacity-30 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-600"
            >
              ◀
            </button>
            <button
              onClick={() => setAuto((v) => !v)}
              className={`flex h-11 w-11 items-center justify-center rounded-full text-sm font-black shadow transition ${
                auto ? 'bg-red-100 ring-1 ring-red-200' : 'text-white'
              }`}
              style={!auto ? { background: '#d9381e' } : { color: '#d9381e' }}
              aria-label={auto ? '暫停自動播放' : '開始自動播放'}
            >
              {auto ? '⏸' : '▶'}
            </button>
            <button
              onClick={() => jump(idx + 1)}
              disabled={idx === total - 1}
              aria-label="下一幕"
              className="rounded-xl bg-white px-3.5 py-2 text-sm font-bold text-stone-600 shadow ring-1 ring-stone-200 disabled:opacity-30 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-600"
            >
              ▶
            </button>
            <div className="ms-2 h-1.5 flex-1 rounded-full bg-stone-200 dark:bg-gray-600">
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${((idx + 1) / total) * 100}%`, background: '#d9381e' }} />
            </div>
            <span className="w-10 text-end font-mono text-xs text-stone-400" dir="ltr">{idx + 1}/{total}</span>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4">
        <h3 className="font-bold text-gray-800 dark:text-white text-sm mb-2">後台四個 TAB，就係為呢個流程而設</h3>
        <div className="grid sm:grid-cols-2 gap-2">
          {[
            { tab: '活動管理', role: '建活動、攞短碼連結、兌換門店＋名額分配（階段一）' },
            { tab: '領取名單', role: '寄出流轉＋到店語義＋Label 列印（階段二）' },
            { tab: '任務跟蹤', role: '提醒時間線＋核實（階段二·三）' },
            { tab: '成效報表', role: '漏斗＋CAC/LTV＋分店導流（階段三）' },
          ].map((t) => (
            <div key={t.tab} className="flex items-center gap-2 text-sm">
              <span className="px-2 py-1 rounded-lg bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 text-xs font-medium whitespace-nowrap">{t.tab}</span>
              <span className="text-gray-600 dark:text-gray-400 text-xs">{t.role}</span>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-stone-400">🔇 右下角喇叭掣可開關背景音樂（瀏覽器限制：需要你點擊一次先會有聲）。</p>
      </div>
    </div>
  );
}
